import throttle from 'lodash/throttle';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import {
  useConsumerQuery,
  useInitiateResetPhoneNumberMutation,
} from 'apollo/queries/autogenerate/hooks';
import Button from 'shared/modules/common/components/atoms/Button';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import { getParsedPhoneNumber } from 'shared/utils/phone';
import validator, { rules } from 'shared/utils/validator';

const FIELD_NAME = 'newPhoneNumber';

type FormProps = {
  newPhoneNumber: string;
};

type Props = {
  onSuccess: (phone: string) => void;
};

const EditUserPhoneEnterNewPhone = (props: Props) => {
  const form = useFormWithValidations({ [FIELD_NAME]: '' });
  const { data } = useConsumerQuery();
  const [initiatePhoneMutation] = useInitiateResetPhoneNumberMutation();
  const [errorMessage, setErrorMessage] = useState('');

  const validatePhone = async () => {
    const newPhone = form.getValues().newPhoneNumber;
    const { isError, message } = rules.mustNotBeSame(
      newPhone.indexOf('81') !== 0 ? '81' + newPhone.substring(1) : newPhone,
      data?.consumer.phone || '',
      '携帯電話番号'
    );

    if (isError) {
      setErrorMessage(message);

      return false;
    }

    const isValid = await form.triggerValidation(FIELD_NAME);

    if (isValid) {
      setErrorMessage('');
      form.clearError(FIELD_NAME);

      return true;
    } else {
      return false;
    }
  };

  const validatePhoneThenSubmit = () => {
    validatePhone().then((isValid: boolean) => {
      if (isValid) {
        submitNewPhone();
      }
    });
  };

  // Sends an OTP code to user's email
  // So this can be invoked to request a new OTP
  const submitNewPhone = throttle(
    async () => {
      try {
        const { newPhoneNumber } = form.getValues();

        await initiatePhoneMutation({
          variables: {
            newPhoneNumber: getParsedPhoneNumber(newPhoneNumber),
          },
        });

        props.onSuccess(newPhoneNumber);
      } catch (err) {
        setErrorMessage('入力内容を確認してください');
      }
    },
    2000,
    { leading: true, trailing: false }
  );

  return (
    <>
      <View style={style.root}>
        <Text style={style.subHeading}>
          新しい携帯電話番号を入力してください。
        </Text>
        <View style={style.spacer} />
        <TextInput
          placeholder="09000000000"
          label="携帯電話番号"
          keyboardType="phone-pad"
          textContentType="telephoneNumber"
          onChangeText={(text) => {
            // Allow only digits
            if (text.match(/[^0-9]/) !== null) {
              return;
            }

            form.setValue(FIELD_NAME, text);
          }}
          value={form.getValues(FIELD_NAME)}
          onFocus={() => {
            setErrorMessage('');
            form.clearError(FIELD_NAME);
          }}
          onBlur={() => {
            setErrorMessage('');
            form.clearError(FIELD_NAME);
            form.triggerValidation(FIELD_NAME);
          }}
          message={
            (form.errors[FIELD_NAME]?.message as string) || errorMessage || ''
          }
          state={
            form.errors[FIELD_NAME]?.message || errorMessage
              ? 'error'
              : undefined
          }
          id="edit-user-phone-new-phone-field"
        />
        <View style={style.spacer} />
      </View>
      <Button
        title="次へ"
        onPress={validatePhoneThenSubmit}
        id="edit-user-phone-next-button"
        style={style.nextButton}
      />
    </>
  );
};

const useFormWithValidations = (props: FormProps) => {
  const form = useForm<FormProps>({
    defaultValues: props,
  });

  useEffect(() => {
    form.register(
      {
        name: 'newPhoneNumber',
      },
      {
        validate: validator.getRules(['mustBeValidPhone']),
      }
    );
  }, []);

  form.watch();

  return form;
};

const style = StyleSheet.create({
  root: {
    flexGrow: 1,
  },
  spacer: {
    marginBottom: 24,
  },
  nextButton: {
    maxWidth: 296,
    alignSelf: 'center',
  },
  subHeading: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '300',
  },
});

export default EditUserPhoneEnterNewPhone;
